import { AppLanguage, getLanguageStrings } from "internationalisation";
import { assertNever } from "../helper";
import { RespondentConfigBuilder } from "../respondentConfigBuilder";
import { WebPlatform } from "../types";
import { generateExternalSurveyTask, generateUploadsTask, generateWebPlatformTask } from "../tasks";

export const VMOM1125CollGroupIds = [
	"VMOM-458-1125-AU-FACEBOOK",
	"VMOM-459-1125-AU-YOUTUBE"
] as const;

export type VMOM1125CollGroupId = typeof VMOM1125CollGroupIds[number];

type VMOM1125CollConfig = {
	groupId: VMOM1125CollGroupId,
	metaFieldNames: ReadonlyArray<string>
}

export const VMOM1125CollWebPlatformIds = [
	"VMOM-1125-AU-FACEBOOK",
	"VMOM-1125-AU-YOUTUBE"
] as const;

export type VMOM1125CollWebPlatformId = typeof VMOM1125CollWebPlatformIds[number];

const getWPTaskLengthMinutes = (webPlatformId: VMOM1125CollWebPlatformId): string => {
	switch (webPlatformId) {
		case "VMOM-1125-AU-FACEBOOK":
			return "7";
		case "VMOM-1125-AU-YOUTUBE":
			return "10";
		default:
			assertNever(webPlatformId);
	}
}

const getWebPlatformId = (groupId: VMOM1125CollGroupId): VMOM1125CollWebPlatformId => {
	switch (groupId) {
		case "VMOM-458-1125-AU-FACEBOOK":
			return "VMOM-1125-AU-FACEBOOK";
		case "VMOM-459-1125-AU-YOUTUBE":
			return "VMOM-1125-AU-YOUTUBE";
		default:
			assertNever(groupId);
	}
}

const getWebPlatformConfigUrl = (webPlatformId: VMOM1125CollWebPlatformId): string => {
	return `https://atrace-cdn.amplifiedintelligence.io/WebPlatform/Configs/VMOM-1125/${webPlatformId}.json`
}

const getWebPlatform = (webPlatformId: VMOM1125CollWebPlatformId): WebPlatform => {
	switch (webPlatformId) {
		case "VMOM-1125-AU-FACEBOOK":
			return "Facebook";
		case "VMOM-1125-AU-YOUTUBE":
			return "Youtube";
		default:
			assertNever(webPlatformId);
	}
}

const getSurveyUrl = (groupId: VMOM1125CollGroupId): string => {
	let surveyId: string;

	switch (groupId) {
		case "VMOM-458-1125-AU-FACEBOOK":
			surveyId = "SV_7OsojWmDpZQ6KUu";
			break;
		case "VMOM-459-1125-AU-YOUTUBE":
			surveyId = "SV_e3Tq291BOVT0OHk";
			break;
		default:
			assertNever(groupId);
	}

	return `https://survey.au1.qualtrics.com/jfe/form/${surveyId}`;
}

export const addVMOM1125CollConfig = (
	config: VMOM1125CollConfig,
	builder: RespondentConfigBuilder
): RespondentConfigBuilder => {
	const FINISH_MESSAGE = "Congratulations! You have now completed all required tasks and your data has been uploaded. Your incentive has been processed. Thank you for your time.";
	const langStrings = getLanguageStrings(AppLanguage.en);

	const webPlatformId = getWebPlatformId(config.groupId);

	const webPlatformTask = generateWebPlatformTask({
		groupId: config.groupId,
		platform: getWebPlatform(webPlatformId),
		webPlatformConfigUrl: getWebPlatformConfigUrl(webPlatformId),
		webPlatformID: webPlatformId,
		taskLengthMinutes: getWPTaskLengthMinutes(webPlatformId),
		useWebPlatformIDForTaskID: true,
		langStrings: getLanguageStrings(AppLanguage.en)
	});

	const externalSurveyTask = generateExternalSurveyTask({
		title: langStrings.Common.survey,
		description: langStrings.Survey.description,
		extraMetaFields: config.metaFieldNames,
		taskID: `${config.groupId}-SURVEY`,
		surveyUrl: getSurveyUrl(config.groupId)
	});

	const uploadsTask = generateUploadsTask({
		config: {
			title: "Uploads"
		},
		taskId: `${config.groupId}-UPLOADS`
	});

	builder.setFlag("deviceLock", "mobile");
	builder.setFlag("requireMinimumBattery", 0.3);
	builder.setFlag("finishMessageOverride", FINISH_MESSAGE);
	builder.setFlag("preventTaskProgress", false);
	builder.addTask(webPlatformTask);
	builder.addTask(externalSurveyTask);
	builder.addTask(uploadsTask);

	return builder;
}